 import { useQuery } from "@tanstack/react-query";
 import { supabase } from "@/integrations/supabase/client";
 
 export interface PartnerCodeRedemption {
   id: string;
   userId: string;
   userName: string;
   userEmail: string;
   redeemedAt: string | null;
   creditsGranted: number;
   eventsBooked: number;
 }
 
 export const useAdminPartnerCodeRedemptions = (partnerCodeId: string | null) => {
   const { data: redemptions = [], isLoading } = useQuery({
     queryKey: ["admin-partner-code-redemptions", partnerCodeId],
     queryFn: async () => {
       if (!partnerCodeId) return [];
 
       // Fetch redemptions for this partner code
       const { data: redemptionsData, error: redemptionsError } = await supabase
         .from("partner_code_redemptions")
         .select("id, user_id, redeemed_at, credits_granted")
         .eq("partner_code_id", partnerCodeId)
         .order("redeemed_at", { ascending: false });
 
       if (redemptionsError) throw redemptionsError;
       if (!redemptionsData || redemptionsData.length === 0) return [];
 
       // Get unique user IDs
       const userIds = [...new Set(redemptionsData.map((r) => r.user_id))];
 
       // Fetch user profiles in batch
       const { data: profiles, error: profilesError } = await supabase
         .from("profiles")
         .select("id, first_name, last_name, email")
         .in("id", userIds);
 
       if (profilesError) throw profilesError;
 
       const profileMap = new Map(
         (profiles || []).map((p) => [
           p.id,
           {
             name: [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown",
             email: p.email || "",
           },
         ])
       );
 
       // Fetch ticket counts per user to measure engagement
       const { data: ticketCounts, error: ticketsError } = await supabase
         .from("tickets")
         .select("user_id")
         .in("user_id", userIds)
         .eq("status", "confirmed");
 
       if (ticketsError) throw ticketsError;
 
       // Count tickets per user
       const ticketCountMap = new Map<string, number>();
       (ticketCounts || []).forEach((t) => {
         ticketCountMap.set(t.user_id, (ticketCountMap.get(t.user_id) || 0) + 1);
       });
 
       // Map redemptions with user data
       return redemptionsData.map((r) => {
         const profile = profileMap.get(r.user_id);
         return {
           id: r.id,
           userId: r.user_id,
           userName: profile?.name || "Unknown",
           userEmail: profile?.email || "",
           redeemedAt: r.redeemed_at,
           creditsGranted: r.credits_granted,
           eventsBooked: ticketCountMap.get(r.user_id) || 0,
         };
       });
     },
     enabled: !!partnerCodeId,
   });
 
   return { redemptions, isLoading };
 };